import { useState, useEffect, useRef } from "react";
import { Drawer, Form, InputNumber, Checkbox, Button, Dropdown, Select, message, Slider, Radio, Modal } from "antd";
import { RgbaStringColorPicker } from "react-colorful";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { slideAction, saveMirrorToCloudinary } from "../../store/slideSlice";
import { deleteElement, findElementType, getUpdatedSlides, updateElementStyle } from "../../utils/helper";
import { slideAPI } from "../../store/api";
import { getSnapshot } from "../../utils/utils";
import { emitElementDelete, emitSlideAdd, emitStyleUpdate } from "../../Socket/Socket";
import "./propertiesDrawer.css";
import { SMART_ART_PALETTES, makeNodeId, makeCustomPalette, CUSTOM_THEME_NAME, smartArtContainerStyles } from "../SmartArt/smartArtUtils";
import { useSmartArtPersist } from "../SmartArt/useSmartArtPersist";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import StraightenIcon from "@mui/icons-material/Straighten";
import smartArtPaneClasses from "../SmartArt/SmartArtTextPane.module.css";

const SMART_ART_LAYOUT_OPTIONS = [
    { value: 'list:vertical', label: 'Vertical List', layout: 'list', variant: 'vertical' },
    { value: 'list:horizontal', label: 'Horizontal List', layout: 'list', variant: 'horizontal' },
    { value: 'process:horizontal', label: 'Process', layout: 'process', variant: 'horizontal' },
    { value: 'process:vertical', label: 'Vertical Process', layout: 'process', variant: 'vertical' },
    { value: 'cycle:', label: 'Cycle', layout: 'cycle', variant: null },
    { value: 'hierarchy:', label: 'Hierarchy', layout: 'hierarchy', variant: null },
    { value: 'pyramid:', label: 'Pyramid', layout: 'pyramid', variant: null },
];

const LAYOUTS_WITH_CONNECTORS = new Set(['process', 'cycle', 'hierarchy']);

const parseRotationDeg = (transform) => {
    if (!transform) return 0;
    const m = String(transform).match(/rotate\(([-\d.]+)deg\)/);
    return m ? parseFloat(m[1]) : 0;
};

// Stable signature of the state-driven values that feed the live canvas update.
// Used so the open/init pass isn't mistaken for a user edit.
const buildSig = (v) => JSON.stringify([
    v.bgColor, v.borderColor, v.strokeUnit, v.borderRadiusUnit, v.borderStyle,
    v.isTransparent, v.opacityValue, v.themeName, v.textColor, v.connectorColor,
    v.customColor, v.smartArtBorderWidth, v.smartArtBorderRadius,
]);

// #rrggbb -> rgba(...) string for the rgba-based color picker; other formats pass through.
const toRgbaString = (color) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(color || ''));
    if (!m) return color;
    const num = parseInt(m[1], 16);
    return `rgba(${(num >> 16) & 255},${(num >> 8) & 255},${num & 255},1)`;
};

const ColorDropdown = ({
    color,
    onChange,
    tooltip = "Pick Color",
    disabled = false,
    icon = <FormatColorFillIcon fontSize="inherit" />,
}) => (
    <Dropdown
        overlay={
            <div className="color-picker-menu">
                <RgbaStringColorPicker color={color} onChange={onChange} />
            </div>
        }
        trigger={["click"]}
        disabled={disabled}
        placement="bottomRight"
    >
        {/* PowerPoint-style split button: icon + live color bar, divider, caret */}
        <button
            type="button"
            className="color-swatch-trigger"
            style={{ "--chip-color": color }}
            onClick={(e) => e.preventDefault()}
            aria-label={tooltip}
            title={tooltip}
        >
            <span className="color-swatch-trigger__preview">
                <span className="color-swatch-trigger__icon">{icon}</span>
                <span className="color-swatch-trigger__bar" />
            </span>
            <span className="color-swatch-trigger__caret">
                <ArrowDropDownIcon fontSize="inherit" />
            </span>
        </button>
    </Dropdown>
);

const PropertiesDrawer = ({ isModalVisible, setShowPropertiesModal, elemRef }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [bgColor, setBgColor] = useState("rgba(255,255,255,1)");
    const [borderColor, setBorderColor] = useState("rgba(0,0,0,1)");
    const [strokeUnit, setStrokeUnit] = useState("px");
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [opacityValue, setOpacityValue] = useState(100);
    const [borderRadiusUnit, setBorderRadiusUnit] = useState("px");
    const [rotationValue, setRotationValue] = useState(0);
    const [borderStyle, setBorderStyle] = useState("solid");
    const [themeName, setThemeName] = useState("blue");
    const [customColor, setCustomColor] = useState("rgba(66,133,244,1)");
    // Container border width/radius for SmartArt (px); shapes use form fields instead.
    const [smartArtBorderWidth, setSmartArtBorderWidth] = useState(1);
    const [smartArtBorderRadius, setSmartArtBorderRadius] = useState(0);
    const [textColor, setTextColor] = useState("rgba(255,255,255,1)");
    const [connectorColor, setConnectorColor] = useState("rgba(154,160,166,1)");
    const [isTransparent, setIsTransparent] = useState(false);
    // WordArt fill/outline effects, edited independently of size/position.
    const [waFillColor, setWaFillColor] = useState("rgba(0,0,0,1)");
    const [waOutlineColor, setWaOutlineColor] = useState("rgba(0,0,0,1)");
    const [waOutlineWidth, setWaOutlineWidth] = useState(0);
    // Position (left/top) as a percentage of the slide, the same units drag/resize
    // store in the element's own styles.
    const [posX, setPosX] = useState(0);
    const [posY, setPosY] = useState(0);
    // Scale is shown as a % of the size the element had when the panel opened
    // (PowerPoint behaviour). baseSizeRef holds that reference size.
    const baseSizeRef = useRef({ width: 100, height: 100 });
    const [scaleHeight, setScaleHeight] = useState(100);
    const [scaleWidth, setScaleWidth] = useState(100);
    const [lockAspect, setLockAspect] = useState(false);
    // Drag-to-reorder state for the SmartArt items list.
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);
    // PowerPoint-style icon tabs: 'fill' | 'effects' | 'size'
    const [activeTab, setActiveTab] = useState("fill");
    // UI-only: which collapsible sections are currently collapsed.
    const [collapsed, setCollapsed] = useState({});
    const toggleSection = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    // PowerPoint-style live editing: edits apply straight to the canvas element
    // (store only, no DB). We snapshot the original slide on open so we can revert
    // if the user closes without saving, and track whether anything is unsaved.
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const originalSlideDataRef = useRef(null);
    // Signature of the last applied state-driven values, so the open/init pass
    // doesn't get mistaken for a user edit.
    const lastSigRef = useRef(null);

    const { slides: existingSlides, mirrorSlides } = useSelector((state) => state.slide);
    const slideId = useSelector((state) => state.slide.currentSlideId);
    const elementId = elemRef.current?.id || null;
    const elementType = findElementType(elemRef?.current);

    const currentSlideData = existingSlides?.[slideId] || {};
    const elementStyles = currentSlideData?.additionalData?.elementsData?.[elementId]?.properties || {};
    const shapeStyles = currentSlideData?.shapes?.[elementId]?.styles || {};
    const imageStyles = currentSlideData?.image?.[elementId]?.styles || {};
    const smartArtData = currentSlideData?.smartArtObjects?.[elementId] || null;
    const wordArtData = currentSlideData?.wordArtTextObjects?.[elementId] || null;
    const { commitNodes: commitSmartArtNodes, commitLayout: commitSmartArtLayout } = useSmartArtPersist(elementId, slideId);
    const smartArtNodes = smartArtData?.nodes || [];

    const updateSmartArtNodeText = (index, value) =>
        commitSmartArtNodes(smartArtNodes.map((n, i) => (i === index ? { ...n, text: value } : n)));
    const addSmartArtItem = () => {
        const lastLevel = smartArtNodes.length ? smartArtNodes[smartArtNodes.length - 1].level : 0;
        commitSmartArtNodes([...smartArtNodes, { id: makeNodeId(), text: 'New item', level: lastLevel }]);
    };
    const removeSmartArtItem = (index) => {
        if (smartArtNodes.length <= 1) return;
        commitSmartArtNodes(smartArtNodes.filter((_, i) => i !== index));
    };
    // Drag-to-reorder items. dragIndex is the row being dragged, overIndex the
    // row it's currently hovering over (for the drop indicator).
    const reorderSmartArtItem = (from, to) => {
        if (from == null || to == null || from === to) return;
        const next = [...smartArtNodes];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        commitSmartArtNodes(next);
    };
    const handleSmartArtLayoutChange = (value) => {
        const opt = SMART_ART_LAYOUT_OPTIONS.find((o) => o.value === value);
        if (opt) commitSmartArtLayout({ layout: opt.layout, variant: opt.variant });
    };
    let currentStyles = {};
    let initialValues = {};

    useEffect(() => {
        if (isModalVisible && elementId && elementType === 'smartart' && smartArtData) {
            const styles = smartArtData.styles || {};
            const theme = smartArtData.theme || {};
            const width = extractNumberFromString(styles.width) || 50;
            const height = extractNumberFromString(styles.height) || 45;
            const opacity = styles.opacity !== undefined ? Math.round(parseFloat(styles.opacity) * 100) : 100;
            const rotation = parseRotationDeg(styles.transform);

            form.setFieldsValue({
                width: parseFloat(Number(width).toFixed(3)),
                height: parseFloat(Number(height).toFixed(3)),
                opacity,
                rotation,
            });
            setOpacityValue(opacity);
            setRotationValue(rotation);
            baseSizeRef.current = { width, height };
            setScaleWidth(100);
            setScaleHeight(100);
            setLockAspect(false);
            setThemeName(theme.name || 'blue');
            const hydratedCustomColor = toRgbaString(theme.customColor || theme.palette?.[0] || SMART_ART_PALETTES.blue[0]);
            setCustomColor(hydratedCustomColor);
            setTextColor(theme.textColor || 'rgba(255,255,255,1)');
            setConnectorColor(theme.connectorColor || 'rgba(154,160,166,1)');
            setPosX(extractNumberFromString(styles.left) || 0);
            setPosY(extractNumberFromString(styles.top) || 0);
            // Container background/border ('transparent'/'none' mean "not set")
            const hydratedBg = styles.backgroundColor && styles.backgroundColor !== 'transparent' ? styles.backgroundColor : null;
            setIsTransparent(!hydratedBg);
            setBgColor(hydratedBg || 'rgba(255,255,255,1)');
            const hydratedBorderStyle = styles.borderStyle === 'solid' ? 'solid' : 'none';
            setBorderStyle(hydratedBorderStyle);
            const hydratedBorderColor = styles.borderColor || 'rgba(0,0,0,1)';
            setBorderColor(hydratedBorderColor);
            const hydratedBorderWidth = extractNumberFromString(styles.borderWidth) || 1;
            setSmartArtBorderWidth(hydratedBorderWidth);
            const hydratedBorderRadius = extractNumberFromString(styles.borderRadius) || 0;
            setSmartArtBorderRadius(hydratedBorderRadius);

            originalSlideDataRef.current = currentSlideData;
            setHasUnsavedChanges(false);
            lastSigRef.current = buildSig({
                bgColor: hydratedBg || 'rgba(255,255,255,1)',
                borderColor: hydratedBorderColor,
                strokeUnit, borderRadiusUnit,
                borderStyle: hydratedBorderStyle,
                isTransparent: !hydratedBg,
                opacityValue: opacity,
                themeName: theme.name || 'blue',
                textColor: theme.textColor || 'rgba(255,255,255,1)',
                connectorColor: theme.connectorColor || 'rgba(154,160,166,1)',
                customColor: hydratedCustomColor,
                smartArtBorderWidth: hydratedBorderWidth,
                smartArtBorderRadius: hydratedBorderRadius,
            });
            return () => form.resetFields();
        }
        if (isModalVisible && elementId && elementType === 'wordart' && wordArtData) {
            const styles = wordArtData.styles || {};
            const effects = wordArtData.effects || {};
            const width = extractNumberFromString(styles.width) || 30;
            const height = extractNumberFromString(styles.height) || 10;
            const opacity = styles.opacity !== undefined ? Math.round(parseFloat(styles.opacity) * 100) : 100;
            const rotation = parseRotationDeg(styles.transform);

            form.setFieldsValue({
                width: parseFloat(Number(width).toFixed(3)),
                height: parseFloat(Number(height).toFixed(3)),
                opacity,
                rotation,
            });
            setOpacityValue(opacity);
            setRotationValue(rotation);
            baseSizeRef.current = { width, height };
            setScaleWidth(100);
            setScaleHeight(100);
            setLockAspect(false);
            setPosX(extractNumberFromString(styles.left) || 0);
            setPosY(extractNumberFromString(styles.top) || 0);
            setWaFillColor(effects.fillType === 'solid' && effects.fillColor ? effects.fillColor : 'rgba(0,0,0,1)');
            setWaOutlineColor(effects.stroke?.color || 'rgba(0,0,0,1)');
            setWaOutlineWidth(effects.stroke ? extractNumberFromString(effects.stroke.width) || 0.5 : 0.5);

            originalSlideDataRef.current = currentSlideData;
            setHasUnsavedChanges(false);
            lastSigRef.current = buildSig({
                bgColor, borderColor, strokeUnit, borderRadiusUnit, borderStyle, isTransparent,
                opacityValue: opacity, themeName, textColor, connectorColor, customColor, smartArtBorderWidth, smartArtBorderRadius,
            });
            return () => form.resetFields();
        }
        if (isModalVisible && elementId) {
            currentStyles =
                elementType === 'shape' ? shapeStyles :
                    elementType === 'image' ? imageStyles :
                        {};

            initialValues = {
                ...elementStyles,
                width: currentStyles?.width || (elementType === 'image' ? 30 : 100),
                height: currentStyles?.height || (elementType === 'image' ? 30 : 100),
                isTransparent: elementStyles?.isTransparent || false,
                strokeWidth: elementStyles?.strokeWidth || (elementType === 'image' ? 0 : 0.5),
                backgroundColor: elementStyles?.backgroundColor || "rgba(255,255,255,1)",
                borderColor: elementStyles?.borderColor || (elementType === 'image' ? '#ffff' : "rgba(0,0,0,1)"),
                strokeUnit: elementStyles?.strokeUnit || "px",
                borderRadius: elementStyles?.borderRadius || 0,
                borderRadiusUnit: elementStyles?.borderRadiusUnit || "px",
                borderStyle: elementStyles?.borderStyle || "solid",
                opacity: elementStyles?.opacity !== undefined ? elementStyles.opacity : 100,
                rotation: elementStyles?.rotation || 0,
            };
            const width = extractNumberFromString(initialValues.width);
            const height = extractNumberFromString(initialValues.height);
            const strokeWidth = extractNumberFromString(initialValues.strokeWidth);
            const borderRadius = extractNumberFromString(initialValues.borderRadius);

            form.setFieldsValue({
                ...initialValues,
                width: !isNaN(width) ? parseFloat(Number(width).toFixed(3)) : 100,
                height: !isNaN(height) ? parseFloat(Number(height).toFixed(3)) : 100,
                strokeWidth: !isNaN(strokeWidth) ? parseFloat(Number(strokeWidth).toFixed(3)) : 0.5,
                borderRadius: !isNaN(borderRadius) ? parseFloat(Number(borderRadius).toFixed(3)) : 0,
                opacity: initialValues.opacity,
            });
            setBgColor(initialValues.backgroundColor);
            setBorderColor(initialValues.borderColor);
            setStrokeUnit(initialValues.strokeUnit);
            setOpacityValue(initialValues.opacity);
            setBorderRadiusUnit(initialValues.borderRadiusUnit);
            setRotationValue(initialValues.rotation);
            setBorderStyle(initialValues.borderStyle);
            setIsTransparent(!!initialValues.isTransparent);
            baseSizeRef.current = {
                width: !isNaN(width) ? parseFloat(Number(width).toFixed(3)) : 100,
                height: !isNaN(height) ? parseFloat(Number(height).toFixed(3)) : 100,
            };
            setScaleWidth(100);
            setScaleHeight(100);
            setLockAspect(false);
            setPosX(extractNumberFromString(currentStyles?.left) || 0);
            setPosY(extractNumberFromString(currentStyles?.top) || 0);

            originalSlideDataRef.current = currentSlideData;
            setHasUnsavedChanges(false);
            lastSigRef.current = buildSig({
                bgColor: initialValues.backgroundColor,
                borderColor: initialValues.borderColor,
                strokeUnit: initialValues.strokeUnit,
                borderRadiusUnit: initialValues.borderRadiusUnit,
                borderStyle: initialValues.borderStyle,
                isTransparent: !!initialValues.isTransparent,
                opacityValue: initialValues.opacity,
                themeName, textColor, connectorColor, customColor, smartArtBorderWidth, smartArtBorderRadius,
            });
        }

        return () => {
            form.resetFields();
        };
    }, [isModalVisible, elementId]);

    // The drawer runs mask-less (so the canvas behind stays interactive). Antd's
    // mask normally locks page scroll; without it the page scrollbar reappears, so
    // re-apply the lock while the drawer is open. We toggle a class (not the inline
    // style) so other portals — e.g. the discard confirm Modal, which captures and
    // restores body.style.overflow — can't leave the page permanently locked. The
    // page's natural scrollbar returns as soon as the class is removed.
    useEffect(() => {
        if (!isModalVisible) return undefined;
        document.body.classList.add('pp-scroll-lock');
        return () => {
            document.body.classList.remove('pp-scroll-lock');
        };
    }, [isModalVisible]);

    // Auto-close when the targeted element is deleted out from under us — e.g.
    // the canvas Delete key or another user over socket. The in-panel Delete
    // button closes itself; this covers every other deletion path. We only close
    // once the element has been confirmed present and then disappears, so element
    // types whose data lives outside these collections never trigger a false close.
    const elementWasPresentRef = useRef(false);
    useEffect(() => {
        if (!isModalVisible || !elementId) {
            elementWasPresentRef.current = false;
            return;
        }
        const exists =
            !!currentSlideData?.shapes?.[elementId] ||
            !!currentSlideData?.image?.[elementId] ||
            !!currentSlideData?.smartArtObjects?.[elementId] ||
            !!currentSlideData?.wordArtTextObjects?.[elementId] ||
            !!currentSlideData?.additionalData?.elementsData?.[elementId];
        if (exists) {
            elementWasPresentRef.current = true;
        } else if (elementWasPresentRef.current) {
            setShowPropertiesModal(false);
        }
    }, [isModalVisible, elementId, currentSlideData]);

    // Build the slide data for a shape/image element from the panel's current values.
    const buildElementSlideData = (updatedData) => ({
        ...currentSlideData,
        additionalData: {
            ...currentSlideData.additionalData,
            elementsData: {
                ...currentSlideData.additionalData?.elementsData,
                [elementId]: {
                    properties: {
                        ...updatedData,
                        height: `${updatedData.height}%`,
                        width: `${updatedData.width}%`,
                        opacity: updatedData.opacity,
                        borderStyle: updatedData.borderStyle,
                        borderRadius: updatedData.borderRadius,
                        borderRadiusUnit: updatedData.borderRadiusUnit
                    },
                },
            },
        },
    });

    const updateSlideData = async (updatedData) => {
        try {
            const slideData = buildElementSlideData(updatedData);

            dispatch(slideAction.addSlide({ slideId, slideData }));

            const dataUrl = await getSnapshot();
            dispatch(saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: id }));

            emitSlideAdd({
                roomId: id,
                mirrorData: { dataId: slideId, mirrorSlideDataUrl: dataUrl },
                slideData: { slideId, slideData },
            });

            const updatedSlides = getUpdatedSlides(slideData, 'slide', existingSlides);
            await slideAPI.addNewSlideInDatabase(
                id, slideId, slideData, dispatch,
                { slides: updatedSlides, mirrorSlides },
            );
        } catch (error) {
            message.error("Failed to update slide. Please try again.");
            throw error;
        }
    };

    const updateSmartArtData = async (values) => {
        const obj = currentSlideData.smartArtObjects?.[elementId];
        if (!obj) return;
        const newStyles = {
            ...obj.styles,
            width: `${values.width}%`,
            height: `${values.height}%`,
            opacity: (values.opacity ?? 100) / 100,
            position: 'absolute',
            left: `${posX}%`,
            top: `${posY}%`,
            ...smartArtContainerStyles({
                isTransparent, bgColor,
                borderOn: borderStyle !== 'none', borderColor, borderWidth: smartArtBorderWidth,
                borderRadius: smartArtBorderRadius,
            }),
        };
        if (values.rotation) newStyles.transform = `rotate(${values.rotation}deg)`;
        else delete newStyles.transform;

        const palette = themeName === CUSTOM_THEME_NAME
            ? makeCustomPalette(customColor)
            : (SMART_ART_PALETTES[themeName] || obj.theme?.palette);
        const newTheme = { ...obj.theme, name: themeName, palette, customColor, textColor, connectorColor };

        // live update
        dispatch(slideAction.updateStyle({ slideId, textBoxId: elementId, updatedStyle: newStyles }));
        dispatch(slideAction.updateSmartArtLayout({ slideId, smartArtId: elementId, theme: newTheme }));
        emitStyleUpdate({ roomId: id, elementData: { slideId, textBoxId: elementId, updatedStyle: newStyles } });

        // snapshot is best-effort — never block the update on it
        try {
            const dataUrl = await getSnapshot();
            dispatch(saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: id }));
        } catch (e) {
            console.error('SmartArt snapshot failed:', e);
        }

        // persist style, then persist theme via the full-document save path
        const slidesAfterStyle = updateElementStyle(
            existingSlides,
            { textBoxId: elementId, updatedStyle: newStyles, currentSlideId: slideId },
            'smartart'
        );
        const slidesAfterTheme = {
            ...slidesAfterStyle,
            [slideId]: {
                ...slidesAfterStyle[slideId],
                smartArtObjects: {
                    ...slidesAfterStyle[slideId].smartArtObjects,
                    [elementId]: { ...slidesAfterStyle[slideId].smartArtObjects[elementId], theme: newTheme },
                },
            },
        };
        await slideAPI.updateSmartArtNodesInDatabase(
            id, slideId, elementId, { nodes: obj.nodes || [], theme: newTheme }, dispatch,
            { slides: slidesAfterTheme, mirrorSlides }
        );
    };

    // WordArt lives in wordArtTextObjects with size/position in styles (as %),
    // opacity in styles, and fill/outline in effects. Size/position/rotation
    // persist through the shared element-style path; fill/outline persist as part
    // of the full element object.
    const updateWordArtData = async (values) => {
        const obj = currentSlideData.wordArtTextObjects?.[elementId];
        if (!obj) return;
        const newStyles = {
            ...obj.styles,
            width: `${values.width}%`,
            height: `${values.height}%`,
            opacity: (values.opacity ?? 100) / 100,
            position: 'absolute',
            left: `${posX}%`,
            top: `${posY}%`,
        };
        if (values.rotation) newStyles.transform = `rotate(${values.rotation}deg)`;
        else delete newStyles.transform;

        // live update
        dispatch(slideAction.updateStyle({ slideId, textBoxId: elementId, updatedStyle: newStyles }));
        emitStyleUpdate({ roomId: id, elementData: { slideId, textBoxId: elementId, updatedStyle: newStyles } });

        // snapshot is best-effort — never block the update on it
        try {
            const dataUrl = await getSnapshot();
            dispatch(saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: id }));
        } catch (e) {
            console.error('WordArt snapshot failed:', e);
        }

        // Persist the full element (styles + any live effect edits) via the same
        // element upsert path used when WordArt is created/duplicated.
        const persistedData = { ...obj, styles: newStyles };
        const updatedSlides = {
            ...existingSlides,
            [slideId]: {
                ...existingSlides[slideId],
                wordArtTextObjects: {
                    ...existingSlides[slideId].wordArtTextObjects,
                    [elementId]: persistedData,
                },
            },
        };
        await slideAPI.createNewElementInDatabase(
            id, slideId, elementId, persistedData, 'wordart', dispatch,
            { slides: updatedSlides, mirrorSlides }
        );
    };

    // Push a WordArt effects change straight to the canvas (store only).
    const applyWordArtEffectsLive = (nextEffects) => {
        const obj = currentSlideData.wordArtTextObjects?.[elementId];
        if (!obj) return;
        dispatch(slideAction.updateElement({
            slideId, elementId, type: 'wordart',
            elementData: { ...obj, effects: nextEffects },
        }));
        setHasUnsavedChanges(true);
    };

    // Text fill picks a solid colour (dropping any gradient); outline is independent.
    const handleWordArtFill = (color) => {
        setWaFillColor(color);
        const obj = currentSlideData.wordArtTextObjects?.[elementId];
        if (!obj) return;
        const effects = { ...obj.effects, fillType: 'solid', fillColor: color };
        delete effects.gradient;
        applyWordArtEffectsLive(effects);
    };

    const handleWordArtOutline = (patch) => {
        const obj = currentSlideData.wordArtTextObjects?.[elementId];
        if (!obj) return;
        const nextColor = patch.color ?? waOutlineColor;
        const nextWidth = patch.width ?? waOutlineWidth;
        setWaOutlineColor(nextColor);
        setWaOutlineWidth(nextWidth);
        const effects = { ...obj.effects };
        if (nextWidth > 0) effects.stroke = { color: nextColor, width: `${nextWidth}px` };
        else delete effects.stroke;
        applyWordArtEffectsLive(effects);
    };

    // Apply the current panel values straight to the canvas element in the store
    // (no DB / snapshot). This is what makes edits visible live, PowerPoint-style.
    const applyLive = () => {
        if (!elementId) return;
        if (elementType === 'smartart') {
            const obj = currentSlideData.smartArtObjects?.[elementId];
            if (!obj) return;
            const values = form.getFieldsValue(true);
            const newStyles = {
                ...obj.styles,
                width: `${values.width}%`,
                height: `${values.height}%`,
                opacity: (values.opacity ?? 100) / 100,
                position: 'absolute',
                left: `${posX}%`,
                top: `${posY}%`,
                ...smartArtContainerStyles({
                    isTransparent, bgColor,
                    borderOn: borderStyle !== 'none', borderColor, borderWidth: smartArtBorderWidth,
                    borderRadius: smartArtBorderRadius,
                }),
            };
            if (values.rotation) newStyles.transform = `rotate(${values.rotation}deg)`;
            else delete newStyles.transform;
            const palette = themeName === CUSTOM_THEME_NAME
                ? makeCustomPalette(customColor)
                : (SMART_ART_PALETTES[themeName] || obj.theme?.palette);
            const newTheme = { ...obj.theme, name: themeName, palette, customColor, textColor, connectorColor };
            dispatch(slideAction.updateStyle({ slideId, textBoxId: elementId, updatedStyle: newStyles }));
            dispatch(slideAction.updateSmartArtLayout({ slideId, smartArtId: elementId, theme: newTheme }));
            return;
        }
        if (elementType === 'wordart') {
            const obj = currentSlideData.wordArtTextObjects?.[elementId];
            if (!obj) return;
            const values = form.getFieldsValue(true);
            const newStyles = {
                ...obj.styles,
                width: `${values.width}%`,
                height: `${values.height}%`,
                opacity: (values.opacity ?? 100) / 100,
                position: 'absolute',
                left: `${posX}%`,
                top: `${posY}%`,
            };
            if (values.rotation) newStyles.transform = `rotate(${values.rotation}deg)`;
            else delete newStyles.transform;
            dispatch(slideAction.updateStyle({ slideId, textBoxId: elementId, updatedStyle: newStyles }));
            return;
        }
        const values = form.getFieldsValue(true);
        const updatedData = {
            ...values,
            backgroundColor: bgColor,
            borderColor,
            strokeUnit,
            elementId,
            borderRadiusUnit,
            borderStyle,
        };
        dispatch(slideAction.addSlide({ slideId, slideData: buildElementSlideData(updatedData) }));
    };

    // Revert the canvas to the slide we snapshotted when the panel opened.
    const revertLive = () => {
        if (originalSlideDataRef.current) {
            dispatch(slideAction.addSlide({ slideId, slideData: originalSlideDataRef.current }));
        }
    };

    // Position lives in the element's own styles (left/top as %), the same place
    // drag/resize write it — separate from size, which the panel keeps in
    // elementsData. These read the current styles for the active element type.
    const elementOwnStyles = () =>
        elementType === 'smartart' ? (smartArtData?.styles || {}) :
            elementType === 'wordart' ? (wordArtData?.styles || {}) :
                elementType === 'image' ? imageStyles :
                    elementType === 'shape' ? shapeStyles : {};

    const positionedStyles = (x, y) => ({
        ...elementOwnStyles(),
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
    });

    // Live-move the element as the user edits the X/Y fields, mirroring a drag.
    const POSITION_TYPES = ['shape', 'image', 'smartart', 'wordart'];
    const handlePositionChange = (axis, value) => {
        const v = value ?? 0;
        const nextX = axis === 'x' ? v : posX;
        const nextY = axis === 'y' ? v : posY;
        if (axis === 'x') setPosX(v); else setPosY(v);
        if (!elementId || !POSITION_TYPES.includes(elementType)) return;
        dispatch(slideAction.updateStyle({
            slideId, textBoxId: elementId, updatedStyle: positionedStyles(nextX, nextY),
        }));
        setHasUnsavedChanges(true);
    };

    // While the panel is open, dragging the element on the canvas updates the store
    // but not the panel's local X/Y snapshot. Mirror the stored position back into
    // the fields so a later in-panel edit (size, rotation, …) doesn't re-apply the
    // stale coordinates and undo the move. Editing the X/Y fields dispatches the same
    // value, so this stays a no-op in that direction.
    useEffect(() => {
        if (!isModalVisible || !elementId || !POSITION_TYPES.includes(elementType)) return;
        const styles = elementOwnStyles();
        const left = extractNumberFromString(styles.left);
        const top = extractNumberFromString(styles.top);
        if (!isNaN(left)) setPosX((prev) => (prev === left ? prev : left));
        if (!isNaN(top)) setPosY((prev) => (prev === top ? prev : top));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalVisible, elementId, elementType,
        shapeStyles?.left, shapeStyles?.top,
        imageStyles?.left, imageStyles?.top,
        smartArtData?.styles?.left, smartArtData?.styles?.top,
        wordArtData?.styles?.left, wordArtData?.styles?.top]);

    // Recompute the Scale % readouts from the current Width/Height vs the size
    // captured when the panel opened.
    const syncScalesFromSize = () => {
        const { width, height } = form.getFieldsValue(['width', 'height']);
        const base = baseSizeRef.current;
        setScaleWidth(base.width ? Math.round((width / base.width) * 100) : 100);
        setScaleHeight(base.height ? Math.round((height / base.height) * 100) : 100);
    };

    // Scale Width/Height inputs resize the element to a % of its base size. With
    // Lock aspect ratio on, both dimensions scale together.
    const handleScaleChange = (axis, value) => {
        const pct = value ?? 100;
        const base = baseSizeRef.current;
        const next = {};
        if (axis === 'height' || lockAspect) {
            next.height = parseFloat(Number(base.height * pct / 100).toFixed(3));
        }
        if (axis === 'width' || lockAspect) {
            next.width = parseFloat(Number(base.width * pct / 100).toFixed(3));
        }
        form.setFieldsValue(next);
        applyLive();
        setHasUnsavedChanges(true);
        syncScalesFromSize();
    };

    // Persist the element's position to the store + DB via the same style path
    // drag/resize use. Shapes/images need this because their Update flow only
    // writes size into elementsData, never the styles where position lives.
    const persistPosition = async () => {
        if (!elementId || (elementType !== 'shape' && elementType !== 'image')) return;
        const updatedStyle = positionedStyles(posX, posY);
        dispatch(slideAction.updateStyle({ slideId, textBoxId: elementId, updatedStyle }));
        emitStyleUpdate({ roomId: id, elementData: { slideId, textBoxId: elementId, updatedStyle } });
        const updatedSlides = updateElementStyle(
            existingSlides,
            { textBoxId: elementId, updatedStyle, currentSlideId: slideId },
            elementType
        );
        await slideAPI.updateElementStyleInDatabase(
            id, slideId, elementId, updatedStyle, elementType, dispatch,
            { slides: updatedSlides, mirrorSlides },
        );
    };

    const handleUpdate = async () => {
        // SmartArt has its own (much smaller) field set — validate only its fields,
        // so unrelated required fields can never make this fail.
        if (elementType === 'smartart') {
            try {
                setIsUpdating(true);
                setIsDeleting(false);
                const values = form.getFieldsValue(true);
                await updateSmartArtData(values);
                setHasUnsavedChanges(false);
                originalSlideDataRef.current = null;
                setShowPropertiesModal(false);
            } catch (error) {
                console.error('SmartArt update failed:', error);
                message.error('Failed to update SmartArt. Please try again.');
            } finally {
                setIsUpdating(false);
            }
            return;
        }
        if (elementType === 'wordart') {
            try {
                setIsUpdating(true);
                setIsDeleting(false);
                const values = form.getFieldsValue(true);
                await updateWordArtData(values);
                setHasUnsavedChanges(false);
                originalSlideDataRef.current = null;
                setShowPropertiesModal(false);
            } catch (error) {
                console.error('WordArt update failed:', error);
                message.error('Failed to update WordArt. Please try again.');
            } finally {
                setIsUpdating(false);
            }
            return;
        }
        try {
            setIsUpdating(true);
            setIsDeleting(false);
            const values = await form.validateFields();
            await updateSlideData({
                ...values,
                backgroundColor: bgColor,
                borderColor,
                strokeUnit,
                elementId,
                borderRadiusUnit,
                borderStyle,
            });
            await persistPosition();
            setHasUnsavedChanges(false);
            originalSlideDataRef.current = null;
            setShowPropertiesModal(false);
        } catch (error) {
            message.error("Invalid input. Please check the form.");
        } finally {
            setIsUpdating(false);
        }
    };

    // Close button / X / mask: warn + discard live edits if anything is unsaved.
    const handleClose = () => {
        if (hasUnsavedChanges) {
            Modal.confirm({
                className: 'pp-confirm',
                title: 'Discard changes?',
                content: 'Your changes have not been saved. If you close now they will be lost.',
                okText: 'Discard changes',
                okButtonProps: { danger: true },
                cancelText: 'Keep editing',
                onOk: () => {
                    revertLive();
                    setHasUnsavedChanges(false);
                    originalSlideDataRef.current = null;
                    setShowPropertiesModal(false);
                },
            });
            return;
        }
        setShowPropertiesModal(false);
    };

    const handleDelete = async () => {
        try {
            setIsDeleting(true);
            setIsUpdating(false);
            const payload = { elementId, slideId };
            const deleteType = elementType === 'image' ? 'image' : elementType === 'smartart' ? 'smartart' : elementType === 'wordart' ? 'wordart' : 'shape';
            const updatedSlides = deleteElement(existingSlides, payload, deleteType);

            await slideAPI.deleteElementFromDatabase(
                id, slideId, elementId, elementType, dispatch,
                { slides: updatedSlides, mirrorSlides },
            );

            elementType === 'shape' && dispatch(slideAction.removeShape({ ...payload, shapeId: elementId }));
            elementType === 'image' && dispatch(slideAction.removeImage({ slideId: slideId, imageId: elementId }));
            elementType === 'smartart' && dispatch(slideAction.removeSmartArtObject({ slideId: slideId, smartArtId: elementId }));
            elementType === 'smartart' && dispatch(slideAction.setActiveSmartArt(null));
            elementType === 'wordart' && dispatch(slideAction.removeWordArtTextObject({ slideId: slideId, wordArtId: elementId }));

            const dataUrl = await getSnapshot();
            dispatch(saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: id }));

            emitElementDelete({ roomId: id, elementType: elementType, elementData: payload });
            setShowPropertiesModal(false);
        } catch (error) {
            message.error("Failed to delete element. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const extractNumberFromString = (value) => {
        if (typeof value === 'string') {
            if (value.includes('%') || value.includes('px') || value.includes('rem')) {
                return parseFloat(value.replace(/[%pxrem]/g, ''));
            }
            return parseFloat(value);
        }
        return value;
    };

    // State-driven controls (colors, units, opacity, transparency, fill/line type,
    // SmartArt theme) flow through here. Form-field controls go through the Form's
    // onValuesChange below. Both push the change live to the canvas. We compare a
    // signature against the values captured on open so the init pass isn't counted
    // as a user edit.
    useEffect(() => {
        if (!isModalVisible || !elementId) return;
        const sig = buildSig({
            bgColor, borderColor, strokeUnit, borderRadiusUnit, borderStyle,
            isTransparent, opacityValue, themeName, textColor, connectorColor, customColor, smartArtBorderWidth, smartArtBorderRadius,
        });
        if (sig === lastSigRef.current) return;
        lastSigRef.current = sig;
        applyLive();
        setHasUnsavedChanges(true);
    }, [bgColor, borderColor, strokeUnit, elementId, borderRadiusUnit, borderStyle, isTransparent, opacityValue, themeName, textColor, connectorColor, customColor, smartArtBorderWidth, smartArtBorderRadius]);

    // Fill type radio (Solid fill / No fill) maps to the existing isTransparent field.
    const handleFillType = (value) => {
        const transparent = value === 'none';
        setIsTransparent(transparent);
        form.setFieldsValue({ isTransparent: transparent });
    };

    // Line type radio (Solid line / No line) maps to the existing borderStyle field.
    const lineType = borderStyle === 'none' ? 'none' : 'solid';
    const handleLineType = (value) => {
        setBorderStyle(value === 'none' ? 'none' : 'solid');
    };

    // PowerPoint shows fill/picture transparency (inverse of our stored opacity).
    const transparencyValue = 100 - opacityValue;
    const setTransparency = (value) => {
        const op = 100 - (value ?? 0);
        setOpacityValue(op);
        form.setFieldsValue({ opacity: op });
    };

    if (!elementId || !isModalVisible) {
        return null;
    }

    const sectionHeader = (key, title) => (
        <button
            type="button"
            className="prop-section__header"
            onClick={() => toggleSection(key)}
            aria-expanded={!collapsed[key]}
        >
            <ArrowDropDownIcon
                fontSize="small"
                className={`prop-section__chevron${collapsed[key] ? ' is-collapsed' : ''}`}
            />
            <span className="prop-section__title">{title}</span>
        </button>
    );
    const sectionBodyClass = (key) => `prop-section__body${collapsed[key] ? ' is-collapsed' : ''}`;

    const disabledSection = (title) => (
        <section className="prop-section prop-section--disabled" key={title}>
            <div className="prop-section__header is-disabled" aria-disabled="true">
                <ArrowDropDownIcon fontSize="small" className="prop-section__chevron is-collapsed" />
                <span className="prop-section__title">{title}</span>
            </div>
        </section>
    );

    const TABS = [
        { id: 'fill', label: 'Fill & Line', icon: <FormatColorFillIcon fontSize="small" /> },
        { id: 'size', label: 'Size & Properties', icon: <StraightenIcon fontSize="small" /> },
    ];
    const panelTitle = elementType === 'image' ? 'Format Picture'
        : elementType === 'smartart' ? 'Format SmartArt'
            : elementType === 'wordart' ? 'Format WordArt' : 'Format Shape';
    // Secondary line under the title — the specific object the panel is editing.
    const smartArtTypeLabel = (() => {
        if (elementType !== 'smartart' || !smartArtData) return '';
        const key = `${smartArtData.layout || 'list'}:${smartArtData.variant || ''}`;
        const opt = SMART_ART_LAYOUT_OPTIONS.find((o) => o.value === key);
        const base = opt ? opt.label : (smartArtData.layout || '');
        return base ? `${base} Diagram` : '';
    })();
    const panelSubtitle = elementType === 'smartart' ? smartArtTypeLabel
        : elementType === 'image' ? 'Picture'
            : elementType === 'wordart' ? 'WordArt'
                : elementType === 'shape' ? 'Shape' : '';
    const panelTitleNode = (
        <div className="pp-title">
            <span className="pp-title__main">{panelTitle}</span>
            {panelSubtitle && <span className="pp-title__sub">{panelSubtitle}</span>}
        </div>
    );
    const panelClass = (id) => `pp-tabpanel${activeTab === id ? '' : ' is-hidden'}`;

    const transparencyRow = () => (
        <div className="slider-row">
            <label className="slider-label">Transparency</label>
            <Slider
                min={0}
                max={100}
                step={1}
                value={transparencyValue}
                onChange={setTransparency}
                tooltip={{ formatter: (v) => `${v}%` }}
            />
            <InputNumber
                className="slider-spin"
                min={0}
                max={100}
                value={transparencyValue}
                formatter={(v) => `${v}%`}
                parser={(v) => String(v).replace('%', '')}
                onChange={(v) => setTransparency(v ?? 0)}
            />
        </div>
    );

    return (
        <Drawer
            title={panelTitleNode}
            placement="right"
            width={360}
            onClose={handleClose}
            visible={isModalVisible}
            destroyOnClose
            mask={false}
            maskClosable={false}
            className="properties-drawer"
            bodyStyle={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', minHeight: 0 }}
            footer={
                <div className="drawer-footer">
                    <Button
                        className={`footer-btn delete-btn btn-style ${isDeleting || isUpdating ? 'disable' : ''}`}
                        onClick={handleDelete}
                    // disabled={isDeleting || isUpdating}
                    >
                        Delete
                    </Button>
                    <Button
                        className={`footer-btn update-btn btn-style ${isDeleting || isUpdating ? 'disable' : ''}`}
                        onClick={handleUpdate}
                        type="primary"
                    // disabled={isDeleting || isUpdating}
                    >
                        Update
                    </Button>
                </div>
            }
        >
            <div className="prop-panel">
                {/* Icon tab bar — Fill & Line / Size & Properties */}
                <div className="pp-tabbar" role="tablist">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === t.id}
                            className={`pp-tab${activeTab === t.id ? ' is-active' : ''}`}
                            onClick={() => setActiveTab(t.id)}
                            title={t.label}
                        >
                            {t.icon}
                            <span className="pp-tab__label">{t.label}</span>
                        </button>
                    ))}
                </div>

                <Form
                    layout="horizontal"
                    form={form}
                    initialValues={initialValues}
                    className="properties-form"
                    onValuesChange={(changed) => {
                        // With aspect locked, editing Width/Height directly drags the
                        // other dimension along using the base aspect ratio.
                        if (lockAspect && (changed.width !== undefined || changed.height !== undefined)) {
                            const base = baseSizeRef.current;
                            const ratio = base.width && base.height ? base.height / base.width : 1;
                            if (changed.width !== undefined) {
                                form.setFieldsValue({ height: parseFloat(Number(changed.width * ratio).toFixed(3)) });
                            } else if (changed.height !== undefined) {
                                form.setFieldsValue({ width: parseFloat(Number(changed.height / ratio).toFixed(3)) });
                            }
                        }
                        // Form-field edits apply straight to the canvas element.
                        applyLive();
                        setHasUnsavedChanges(true);
                        syncScalesFromSize();
                    }}
                >
                    {/* Keep these fields registered for validateFields, even when their
                        tab/control is hidden — they're driven by the controls below. */}
                    <Form.Item name="opacity" hidden><InputNumber /></Form.Item>
                    {elementType === 'shape' && (
                        <Form.Item name="isTransparent" hidden valuePropName="checked"><Checkbox /></Form.Item>
                    )}

                    {/* ============ FILL & LINE TAB ============ */}
                    <div className={panelClass('fill')}>
                        {elementType === 'shape' && (
                            <section className="prop-section">
                                {sectionHeader('fill', 'Fill')}
                                <div className={sectionBodyClass('fill')}>
                                    <Radio.Group
                                        className="pp-radio-group"
                                        value={isTransparent ? 'none' : 'solid'}
                                        onChange={(e) => handleFillType(e.target.value)}
                                    >
                                        <Radio value="none">No fill</Radio>
                                        <Radio value="solid">Solid fill</Radio>
                                    </Radio.Group>
                                    {!isTransparent && (
                                        <div className="pp-radio-detail">
                                            <div className="prop-row">
                                                <label className="prop-row__label">Color</label>
                                                <div className="color-picker-container">
                                                    <ColorDropdown color={bgColor} onChange={setBgColor} tooltip="Fill Color" icon={<FormatColorFillIcon fontSize="inherit" />} />
                                                </div>
                                            </div>
                                            {transparencyRow()}
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {elementType !== 'smartart' && elementType !== 'wordart' && (
                            <section className="prop-section">
                                {sectionHeader('line', 'Line')}
                                <div className={sectionBodyClass('line')}>
                                    <div className="prop-row">
                                        <label className="prop-row__label">Color</label>
                                        <div className="color-picker-container">
                                            <ColorDropdown color={borderColor} onChange={setBorderColor} tooltip="Line Color" icon={<BorderColorIcon fontSize="inherit" />} />
                                        </div>
                                    </div>
                                    <div className="prop-field">
                                        <label className="prop-field__label">Width</label>
                                        <div className="stroke-width-container">
                                            <Form.Item
                                                name="strokeWidth"
                                                noStyle
                                                rules={[{ required: true, message: "Required" }]}
                                            >
                                                <InputNumber min={0} step={0.1} precision={2} className="stroke-width-input" />
                                            </Form.Item>
                                            <Form.Item name="strokeUnit" noStyle>
                                                <Select
                                                    dropdownClassName="pp-dropdown"
                                                    value={strokeUnit}
                                                    onChange={setStrokeUnit}
                                                    options={[
                                                        { value: "px", label: "px" },
                                                        { value: "rem", label: "rem" },
                                                        elementType === 'shape' ? { value: "%", label: "%" } : null,
                                                    ].filter(Boolean)}
                                                    className="stroke-unit-select"
                                                />
                                            </Form.Item>
                                        </div>
                                    </div>
                                    {elementType === 'image' && (
                                        <div className="prop-field">
                                            <label className="prop-field__label">Dash type</label>
                                            <Form.Item name="borderStyle" noStyle>
                                                <Select
                                                    dropdownClassName="pp-dropdown"
                                                    value={borderStyle}
                                                    onChange={setBorderStyle}
                                                    options={[
                                                        { value: "solid", label: "Solid" },
                                                        { value: "dashed", label: "Dashed" },
                                                        { value: "dotted", label: "Dotted" },
                                                        { value: "groove", label: "Groove" },
                                                        { value: "double", label: "Double" },
                                                        { value: "ridge", label: "Ridge" },
                                                        { value: "none", label: "None" },
                                                    ]}
                                                    className="stroke-unit-select full-width-select"
                                                />
                                            </Form.Item>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* SmartArt fill/line equivalents — layout & theme */}
                        {elementType === 'smartart' && (
                            <>
                                <section className="prop-section">
                                    {sectionHeader('sa-bg', 'Background')}
                                    <div className={sectionBodyClass('sa-bg')}>
                                        <Radio.Group
                                            className="pp-radio-group"
                                            value={isTransparent ? 'none' : 'solid'}
                                            onChange={(e) => handleFillType(e.target.value)}
                                        >
                                            <Radio value="none">No fill</Radio>
                                            <Radio value="solid">Solid fill</Radio>
                                        </Radio.Group>
                                        {!isTransparent && (
                                            <div className="pp-radio-detail">
                                                <div className="prop-row">
                                                    <label className="prop-row__label">Color</label>
                                                    <div className="color-picker-container">
                                                        <ColorDropdown color={bgColor} onChange={setBgColor} tooltip="Background Color" icon={<FormatColorFillIcon fontSize="inherit" />} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <section className="prop-section">
                                    {sectionHeader('sa-border', 'Border')}
                                    <div className={sectionBodyClass('sa-border')}>
                                        <Radio.Group
                                            className="pp-radio-group"
                                            value={lineType}
                                            onChange={(e) => handleLineType(e.target.value)}
                                        >
                                            <Radio value="none">No line</Radio>
                                            <Radio value="solid">Solid line</Radio>
                                        </Radio.Group>
                                        {lineType === 'solid' && (
                                            <div className="pp-radio-detail">
                                                <div className="prop-row">
                                                    <label className="prop-row__label">Color</label>
                                                    <div className="color-picker-container">
                                                        <ColorDropdown color={borderColor} onChange={setBorderColor} tooltip="Border Color" icon={<BorderColorIcon fontSize="inherit" />} />
                                                    </div>
                                                </div>
                                                <div className="prop-field">
                                                    <label className="prop-field__label">Width</label>
                                                    <InputNumber
                                                        min={0.5}
                                                        max={10}
                                                        step={0.5}
                                                        value={smartArtBorderWidth}
                                                        onChange={(v) => setSmartArtBorderWidth(v ?? 1)}
                                                        className="stroke-width-input"
                                                    />
                                                </div>
                                                <div className="prop-field">
                                                    <label className="prop-field__label">Corner radius</label>
                                                    <InputNumber
                                                        min={0}
                                                        max={50}
                                                        step={1}
                                                        value={smartArtBorderRadius}
                                                        onChange={(v) => setSmartArtBorderRadius(v ?? 0)}
                                                        className="stroke-width-input"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <section className="prop-section">
                                    {sectionHeader('layout', 'Layout')}
                                    <div className={sectionBodyClass('layout')}>
                                        <Form.Item className="compact-form-item" colon={false} noStyle>
                                            <Select
                                                dropdownClassName="pp-dropdown"
                                                value={`${smartArtData?.layout || 'list'}:${smartArtData?.variant || ''}`}
                                                onChange={handleSmartArtLayoutChange}
                                                options={SMART_ART_LAYOUT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </div>
                                </section>

                                <section className="prop-section">
                                    {sectionHeader('theme', 'SmartArt Theme')}
                                    <div className={sectionBodyClass('theme')}>
                                        <div className="prop-field">
                                            <label className="prop-field__label">Color Theme</label>
                                            <div className="sa-theme-swatches">
                                                {Object.entries(SMART_ART_PALETTES).map(([name, palette]) => {
                                                    const label = name.charAt(0).toUpperCase() + name.slice(1);
                                                    const active = themeName === name;
                                                    return (
                                                        <button
                                                            key={name}
                                                            type="button"
                                                            className={`sa-theme-swatch${active ? ' sa-theme-swatch--active' : ''}`}
                                                            onClick={() => setThemeName(name)}
                                                            aria-pressed={active}
                                                            aria-label={label}
                                                            title={label}
                                                        >
                                                            <span className="sa-theme-swatch__preview">
                                                                {palette.slice(0, 4).map((c, i) => (
                                                                    <span key={i} className="sa-theme-swatch__seg" style={{ background: c }} />
                                                                ))}
                                                            </span>
                                                            <span className="sa-theme-swatch__label">{label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="sa-theme-custom-row">
                                            <button
                                                type="button"
                                                className={`sa-theme-swatch${themeName === CUSTOM_THEME_NAME ? ' sa-theme-swatch--active' : ''}`}
                                                onClick={() => setThemeName(CUSTOM_THEME_NAME)}
                                                aria-pressed={themeName === CUSTOM_THEME_NAME}
                                                aria-label="Custom theme"
                                                title="Custom theme"
                                            >
                                                <span className="sa-theme-swatch__preview">
                                                    {makeCustomPalette(customColor).slice(0, 4).map((c, i) => (
                                                        <span key={i} className="sa-theme-swatch__seg" style={{ background: c }} />
                                                    ))}
                                                </span>
                                                <span className="sa-theme-swatch__label">Custom</span>
                                            </button>
                                            <div className="color-picker-container">
                                                <ColorDropdown
                                                    color={customColor}
                                                    onChange={(c) => { setCustomColor(c); setThemeName(CUSTOM_THEME_NAME); }}
                                                    tooltip="Custom Color"
                                                />
                                            </div>
                                        </div>
                                        {LAYOUTS_WITH_CONNECTORS.has(smartArtData?.layout) && (
                                            <div className="prop-row">
                                                <label className="prop-row__label">Connector Color</label>
                                                <div className="color-picker-container">
                                                    <ColorDropdown color={connectorColor} onChange={setConnectorColor} tooltip="Connector Color" icon={<BorderColorIcon fontSize="inherit" />} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            </>
                        )}

                        {/* WordArt fill/line equivalents — text fill & outline */}
                        {elementType === 'wordart' && (
                            <>
                                <section className="prop-section">
                                    {sectionHeader('wa-fill', 'Text Fill')}
                                    <div className={sectionBodyClass('wa-fill')}>
                                        <div className="prop-row">
                                            <label className="prop-row__label">Color</label>
                                            <div className="color-picker-container">
                                                <ColorDropdown color={waFillColor} onChange={handleWordArtFill} tooltip="Text Fill Color" icon={<FormatColorTextIcon fontSize="inherit" />} />
                                            </div>
                                        </div>
                                        <p className="pp-hint">Applies a solid fill (replaces any gradient preset).</p>
                                    </div>
                                </section>

                                <section className="prop-section">
                                    {sectionHeader('wa-outline', 'Text Outline')}
                                    <div className={sectionBodyClass('wa-outline')}>
                                        <div className="prop-row">
                                            <label className="prop-row__label">Color</label>
                                            <div className="color-picker-container">
                                                <ColorDropdown color={waOutlineColor} onChange={(c) => handleWordArtOutline({ color: c })} tooltip="Outline Color" icon={<BorderColorIcon fontSize="inherit" />} />
                                            </div>
                                        </div>
                                        <div className="prop-field">
                                            <label className="prop-field__label">Width</label>
                                            <InputNumber
                                                min={0}
                                                max={20}
                                                step={0.5}
                                                precision={2}
                                                value={waOutlineWidth}
                                                onChange={(v) => handleWordArtOutline({ width: v ?? 0 })}
                                                addonAfter="px"
                                            />
                                        </div>
                                        <p className="pp-hint">Set width to 0 to remove the outline.</p>
                                    </div>
                                </section>
                            </>
                        )}
                    </div>

                    {/* ============ EFFECTS TAB (disabled placeholders) ============ */}
                    <div className={panelClass('effects')}>
                        {['Shadow', 'Reflection', 'Glow', 'Soft Edges', '3-D Format', '3-D Rotation'].map(disabledSection)}
                    </div>

                    {/* ============ SIZE & PROPERTIES TAB ============ */}
                    <div className={panelClass('size')}>
                        <section className="prop-section">
                            {sectionHeader('size', 'Size')}
                            <div className={sectionBodyClass('size')}>
                                <div className="dim-grid">
                                    <div className="prop-field">
                                        <label className="prop-field__label">Height</label>
                                        <Form.Item
                                            name="height"
                                            rules={[{ required: true, message: "Height is required" }]}
                                            className="prop-field__control"
                                        >
                                            <InputNumber
                                                min={10}
                                                precision={3}
                                                formatter={(value) => {
                                                    const num = extractNumberFromString(value);
                                                    return !isNaN(num) ? parseFloat(Number(num).toFixed(3)) : '';
                                                }}
                                                parser={(value) => {
                                                    const num = extractNumberFromString(value);
                                                    return !isNaN(num) ? parseFloat(num) : 0;
                                                }}
                                                addonAfter={elementType === 'smartart' || elementType === 'wordart' ? '%' : 'px'}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className="prop-field">
                                        <label className="prop-field__label">Width</label>
                                        <Form.Item
                                            name="width"
                                            rules={[{ required: true, message: "Width is required" }]}
                                            className="prop-field__control"
                                        >
                                            <InputNumber
                                                min={1}
                                                precision={3}
                                                formatter={(value) => {
                                                    const num = extractNumberFromString(value);
                                                    return !isNaN(num) ? parseFloat(Number(num).toFixed(3)) : '';
                                                }}
                                                parser={(value) => {
                                                    const num = extractNumberFromString(value);
                                                    return !isNaN(num) ? parseFloat(num) : 0;
                                                }}
                                                addonAfter={elementType === 'smartart' || elementType === 'wordart' ? '%' : 'px'}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                <div className="slider-row">
                                    <label className="slider-label">Rotation</label>
                                    <Form.Item name="rotation" noStyle>
                                        <Slider
                                            min={0}
                                            max={360}
                                            step={1}
                                            onChange={(value) => {
                                                setRotationValue(value);
                                                form.setFieldsValue({ rotation: value });
                                            }}
                                            tooltip={{ formatter: (value) => `${value}°` }}
                                        />
                                    </Form.Item>
                                    <InputNumber
                                        className="slider-spin"
                                        min={0}
                                        max={360}
                                        value={rotationValue}
                                        formatter={(value) => `${value}°`}
                                        parser={(value) => String(value).replace('°', '')}
                                        onChange={(value) => {
                                            const v = value ?? 0;
                                            setRotationValue(v);
                                            form.setFieldsValue({ rotation: v });
                                        }}
                                    />
                                </div>

                                {/* Transparency lives here too so it's always reachable */}
                                {elementType !== 'shape' && transparencyRow()}

                                {elementType === 'image' && (
                                    <div className="prop-field">
                                        <label className="prop-field__label">Corner radius</label>
                                        <div className="stroke-width-container">
                                            <Form.Item
                                                name="borderRadius"
                                                noStyle
                                                rules={[{ required: true, message: "Required" }]}
                                            >
                                                <InputNumber min={0} step={0.1} precision={2} className="stroke-width-input" />
                                            </Form.Item>
                                            <Form.Item name="borderRadiusUnit" noStyle>
                                                <Select
                                                    dropdownClassName="pp-dropdown"
                                                    value={borderRadiusUnit}
                                                    onChange={setBorderRadiusUnit}
                                                    options={[
                                                        { value: "px", label: "px" },
                                                        { value: "%", label: "%" },
                                                        { value: "rem", label: "rem" },
                                                    ]}
                                                    className="stroke-unit-select"
                                                />
                                            </Form.Item>
                                        </div>
                                    </div>
                                )}

                                <div className="prop-row">
                                    <label className="prop-row__label">Scale height</label>
                                    <InputNumber
                                        min={1}
                                        value={scaleHeight}
                                        formatter={(v) => `${v}%`}
                                        parser={(v) => String(v).replace('%', '')}
                                        onChange={(v) => handleScaleChange('height', v)}
                                        className="pp-scale-input"
                                    />
                                </div>
                                <div className="prop-row">
                                    <label className="prop-row__label">Scale width</label>
                                    <InputNumber
                                        min={1}
                                        value={scaleWidth}
                                        formatter={(v) => `${v}%`}
                                        parser={(v) => String(v).replace('%', '')}
                                        onChange={(v) => handleScaleChange('width', v)}
                                        className="pp-scale-input"
                                    />
                                </div>
                                <Checkbox
                                    className="checkbox-item"
                                    checked={lockAspect}
                                    onChange={(e) => setLockAspect(e.target.checked)}
                                >
                                    Lock aspect ratio
                                </Checkbox>
                            </div>
                        </section>

                        {POSITION_TYPES.includes(elementType) ? (
                            <section className="prop-section">
                                {sectionHeader('position', 'Position')}
                                <div className={sectionBodyClass('position')}>
                                    <div className="dim-grid">
                                        <div className="prop-field">
                                            <label className="prop-field__label">Horizontal</label>
                                            <div className="prop-field__control">
                                                <InputNumber
                                                    value={posX}
                                                    precision={2}
                                                    step={0.5}
                                                    onChange={(v) => handlePositionChange('x', v)}
                                                    addonAfter="%"
                                                />
                                            </div>
                                        </div>
                                        <div className="prop-field">
                                            <label className="prop-field__label">Vertical</label>
                                            <div className="prop-field__control">
                                                <InputNumber
                                                    value={posY}
                                                    precision={2}
                                                    step={0.5}
                                                    onChange={(v) => handlePositionChange('y', v)}
                                                    addonAfter="%"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <p className="pp-hint">Distance from the slide's top-left corner.</p>
                                </div>
                            </section>
                        ) : (
                            disabledSection('Position')
                        )}

                        {/* SmartArt content editor */}
                        {elementType === 'smartart' && (
                            <section className="prop-section">
                                {sectionHeader('items', 'Items')}
                                <div className={sectionBodyClass('items')}>
                                    <div className={smartArtPaneClasses.itemList} style={{ maxHeight: 'none', overflow: 'visible' }}>
                                        {smartArtNodes.map((node, index) => (
                                            <div
                                                className={smartArtPaneClasses.itemRow}
                                                key={node.id}
                                                onDragOver={(e) => {
                                                    if (dragIndex == null) return;
                                                    e.preventDefault();
                                                    if (overIndex !== index) setOverIndex(index);
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    reorderSmartArtItem(dragIndex, index);
                                                    setDragIndex(null);
                                                    setOverIndex(null);
                                                }}
                                                style={{
                                                    opacity: dragIndex === index ? 0.4 : 1,
                                                    borderTop: overIndex === index && dragIndex !== index
                                                        ? '2px solid var(--pp-accent, #4f6bed)' : '2px solid transparent',
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    className={smartArtPaneClasses.iconBtn}
                                                    title="Drag to reorder"
                                                    draggable
                                                    onDragStart={(e) => {
                                                        setDragIndex(index);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        e.dataTransfer.setData('text/plain', String(index));
                                                    }}
                                                    onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                                                    style={{ cursor: 'grab' }}
                                                >
                                                    <DragIndicatorIcon fontSize="small" />
                                                </button>
                                                <input
                                                    className={smartArtPaneClasses.itemInput}
                                                    value={node.text}
                                                    style={{ marginLeft: node.level * 8 }}
                                                    onChange={(e) => updateSmartArtNodeText(index, e.target.value)}
                                                    placeholder="Item text"
                                                />
                                                <button
                                                    type="button"
                                                    className={`${smartArtPaneClasses.iconBtn} ${smartArtPaneClasses.removeBtn}`}
                                                    title="Remove item"
                                                    disabled={smartArtNodes.length <= 1}
                                                    onClick={() => removeSmartArtItem(index)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className={smartArtPaneClasses.addRow}>
                                        <button type="button" className={smartArtPaneClasses.addBtn} onClick={addSmartArtItem}>
                                            <AddIcon fontSize="small" /> Add item
                                        </button>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>
                </Form>
            </div>
        </Drawer>
    );
};

export default PropertiesDrawer;